import { getBackendUrl, translateAndSlugify } from "../../lib/utils";
import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { 
  Save, Plus, Trash2, Edit, AlertCircle, CheckCircle2, ChevronRight, HelpCircle, FileText,
  Bold, Italic, Underline, Heading1, Heading2, List, Link, Link2Off, Eraser
} from 'lucide-react';

const RichTextEditor = ({ value, onChange }) => {
  const editorRef = useRef(null);

  // Sync value from state to editor div on initial mount or when value changes externally
  useEffect(() => {
    if (editorRef.current && editorRef.current.innerHTML !== value) {
      editorRef.current.innerHTML = value || '<p><br></p>';
    }
  }, [value]);

  const execCommand = (command, arg = null) => {
    document.execCommand(command, false, arg);
    if (editorRef.current) {
      onChange(editorRef.current.innerHTML);
    }
  };

  const handleInput = () => {
    if (editorRef.current) {
      onChange(editorRef.current.innerHTML);
    }
  };

  return (
    <div className="border border-slate-300 rounded-2xl overflow-hidden bg-white focus-within:ring-2 focus-within:ring-blue-500/50 focus-within:border-blue-500 transition-all shadow-sm">
      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-1.5 bg-slate-50 border-b border-slate-200 p-2.5 text-slate-600">
        <button
          type="button"
          onClick={() => execCommand('bold')}
          className="p-2 hover:bg-slate-200 rounded-xl font-bold text-slate-700 hover:text-slate-900 transition-colors w-9 h-9 flex items-center justify-center"
          title="Kalın (Bold)"
        >
          <Bold size={16} />
        </button>
        <button
          type="button"
          onClick={() => execCommand('italic')}
          className="p-2 hover:bg-slate-200 rounded-xl font-bold text-slate-700 hover:text-slate-900 transition-colors w-9 h-9 flex items-center justify-center"
          title="İtalik (Italic)"
        >
          <Italic size={16} />
        </button>
        <button
          type="button"
          onClick={() => execCommand('underline')}
          className="p-2 hover:bg-slate-200 rounded-xl font-bold text-slate-700 hover:text-slate-900 transition-colors w-9 h-9 flex items-center justify-center"
          title="Altı Çizili (Underline)"
        >
          <Underline size={16} />
        </button>
        <div className="w-px h-6 bg-slate-200 mx-1"></div>
        <button
          type="button"
          onClick={() => execCommand('formatBlock', '<h1>')}
          className="p-2 hover:bg-slate-200 rounded-xl font-bold text-slate-700 hover:text-slate-900 transition-colors w-9 h-9 flex items-center justify-center"
          title="Başlık 1 (H1)"
        >
          <Heading1 size={16} />
        </button>
        <button
          type="button"
          onClick={() => execCommand('formatBlock', '<h2>')}
          className="p-2 hover:bg-slate-200 rounded-xl font-bold text-slate-700 hover:text-slate-900 transition-colors w-9 h-9 flex items-center justify-center"
          title="Başlık 2 (H2)"
        >
          <Heading2 size={16} />
        </button>
        <button
          type="button"
          onClick={() => execCommand('formatBlock', '<p>')}
          className="p-2 hover:bg-slate-200 rounded-xl text-xs font-extrabold text-slate-700 hover:text-slate-900 transition-colors w-9 h-9 flex items-center justify-center"
          title="Paragraf (P)"
        >
          P
        </button>
        <div className="w-px h-6 bg-slate-200 mx-1"></div>
        <button
          type="button"
          onClick={() => execCommand('insertUnorderedList')}
          className="p-2 hover:bg-slate-200 rounded-xl font-bold text-slate-700 hover:text-slate-900 transition-colors w-9 h-9 flex items-center justify-center"
          title="Sırasız Liste (List)"
        >
          <List size={16} />
        </button>
        <button
          type="button"
          onClick={() => {
            const url = window.prompt("Bağlantı adresi (URL) girin:");
            if (url) execCommand('createLink', url);
          }}
          className="p-2 hover:bg-slate-200 rounded-xl font-bold text-blue-600 hover:text-blue-700 transition-colors w-9 h-9 flex items-center justify-center"
          title="Bağlantı Ekle (Link)"
        >
          <Link size={16} />
        </button>
        <button
          type="button"
          onClick={() => execCommand('unlink')}
          className="p-2 hover:bg-slate-200 rounded-xl font-bold text-red-500 hover:text-red-600 transition-colors w-9 h-9 flex items-center justify-center"
          title="Bağlantıyı Kaldır (Unlink)"
        >
          <Link2Off size={16} />
        </button>
        <div className="w-px h-6 bg-slate-200 mx-1"></div>
        <button
          type="button"
          onClick={() => execCommand('removeFormat')}
          className="p-2 hover:bg-slate-200 rounded-xl font-bold text-slate-700 hover:text-slate-900 transition-colors w-9 h-9 flex items-center justify-center"
          title="Biçimlendirmeyi Temizle (Clear Format)"
        >
          <Eraser size={16} />
        </button>
      </div>
      {/* Editor Content Area */}
      <div
        ref={editorRef}
        contentEditable
        onInput={handleInput}
        className="p-5 min-h-[350px] max-h-[500px] overflow-y-auto outline-none bg-white prose prose-slate max-w-none text-slate-800 text-sm leading-relaxed"
        style={{ direction: 'ltr' }}
      />
    </div>
  );
};

const AdminPages = () => {
  const [pages, setPages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');
  
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [formData, setFormData] = useState({
    id: '', title: '', slug: '', content: '', seo_title: '', seo_description: ''
  });

  useEffect(() => {
    fetchPages();
  }, []);

  const fetchPages = async () => {
    setLoading(true);
    try {
      const backendUrl = getBackendUrl();
      const res = await axios.get(`${backendUrl}/api/pages`);
      setPages(res.data);
    } catch (err) {
      console.error("Error loading pages", err);
      setError("Sayfalar yüklenirken bir hata oluştu.");
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (page) => {
    setFormData({
      id: page.id,
      title: page.title,
      slug: page.slug,
      content: page.content,
      seo_title: page.seo_title || '',
      seo_description: page.seo_description || ''
    });
    setIsFormOpen(true);
    setSuccess('');
    setError('');
  };

  const handleCreate = () => {
    setFormData({
      id: '', title: '', slug: '', content: '', seo_title: '', seo_description: ''
    });
    setIsFormOpen(true);
    setSuccess('');
    setError('');
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Bu sayfayı silmek istediğinizden emin misiniz?")) return;
    setLoading(true);
    setSuccess('');
    setError('');
    try {
      const backendUrl = getBackendUrl();
      await axios.delete(`${backendUrl}/api/pages/${id}`, { withCredentials: true });
      setSuccess("Sayfa başarıyla silindi!");
      setTimeout(() => setSuccess(''), 3000);
      fetchPages();
    } catch (err) {
      console.error("Error deleting page", err);
      setError("Sayfa silinirken hata oluştu.");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setSuccess('');
    setError('');
    try {
      const backendUrl = getBackendUrl();
      await axios.post(`${backendUrl}/api/pages`, formData, { withCredentials: true });
      setSuccess(formData.id ? "Sayfa başarıyla güncellendi!" : "Yeni sayfa oluşturuldu!");
      setTimeout(() => setSuccess(''), 3000);
      setIsFormOpen(false);
      fetchPages();
    } catch (err) {
      console.error("Error saving page", err);
      setError("Sayfa kaydedilirken hata oluştu: " + (err.response?.data?.detail || err.message));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-white rounded-3xl border border-slate-200/80 p-6 md:p-8 shadow-sm">
      {/* Toast Alert */}
      {(success || error) && (
        <div className="fixed top-24 right-8 z-50 animate-bounce">
          {success && (
            <div className="bg-emerald-50 text-emerald-800 border border-emerald-200 rounded-2xl px-6 py-4 flex items-center gap-3 shadow-xl">
              <CheckCircle2 size={24} className="text-emerald-500" />
              <span className="font-bold text-sm">{success}</span>
            </div>
          )}
          {error && (
            <div className="bg-red-50 text-red-800 border border-red-200 rounded-2xl px-6 py-4 flex items-center gap-3 shadow-xl">
              <AlertCircle size={24} className="text-red-500" />
              <span className="font-bold text-sm">{error}</span>
            </div>
          )}
        </div>
      )}

      {/* Header */}
      <div className="flex justify-between items-center mb-8">
        <div>
          <h2 className="text-2xl font-extrabold text-slate-800">Sayfa ve Sözleşmeler Yönetimi</h2>
          <p className="text-sm text-slate-500 mt-1">KVKK, Gizlilik Politikası gibi genel kurumsal bilgi sayfalarını yönetin.</p>
        </div>
        {!isFormOpen && (
          <button 
            onClick={handleCreate}
            className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-5 py-3 rounded-xl text-sm font-bold shadow-md shadow-blue-600/10 transition-all"
          >
            <Plus size={18} /> Yeni Sayfa Ekle
          </button>
        )}
      </div>

      {loading && <div className="text-blue-600 font-bold mb-4 flex items-center gap-2 text-sm"><HelpCircle className="animate-spin" size={18} /> Yükleniyor / İşleniyor...</div>}

      {isFormOpen ? (
        /* Form Section */
        <form onSubmit={handleSubmit} className="space-y-6 bg-slate-50 border border-slate-200/60 p-6 md:p-8 rounded-2xl">
          <div className="flex items-center justify-between border-b border-slate-200 pb-4 mb-4">
            <h3 className="font-extrabold text-slate-800 text-lg">{formData.id ? "Sayfayı Düzenle" : "Yeni Sayfa Ekle"}</h3>
            <button 
              type="button" 
              onClick={() => setIsFormOpen(false)}
              className="text-slate-500 hover:text-slate-800 text-sm font-bold px-3 py-1.5 rounded-lg hover:bg-slate-100 transition"
            >
              Vazgeç
            </button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-4">
              <div>
                <label className="block text-xs font-bold text-slate-500 mb-1">Sayfa Başlığı *</label>
                <input 
                  required
                  type="text" 
                  value={formData.title} 
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  onBlur={async () => {
                    if (!formData.slug && formData.title) {
                      const ts = await translateAndSlugify(formData.title);
                      setFormData(prev => ({ ...prev, slug: ts }));
                    }
                  }}
                  placeholder="Örn: KVKK Aydınlatma Metni"
                  className="w-full px-4 py-2.5 bg-white border border-slate-300 rounded-xl text-sm font-semibold focus:outline-none focus:ring-2 focus:ring-blue-500" 
                />
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-500 mb-1">Sayfa Adresi (URL / Slug) (Boş bırakırsanız otomatik oluşturulur)</label>
                <input 
                  type="text" 
                  value={formData.slug} 
                  onChange={(e) => setFormData({ ...formData, slug: e.target.value })}
                  placeholder="Örn: kvkk-aydinlatma-metni"
                  className="w-full px-4 py-2.5 bg-white border border-slate-300 rounded-xl text-sm font-semibold focus:outline-none focus:ring-2 focus:ring-blue-500" 
                />
              </div>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-xs font-bold text-slate-500 mb-1">SEO Sayfa Başlığı (Boşsa normal başlık kullanılır)</label>
                <input 
                  type="text" 
                  value={formData.seo_title} 
                  onChange={(e) => setFormData({ ...formData, seo_title: e.target.value })}
                  placeholder="Örn: KVKK Metni - İSİLKOOP"
                  className="w-full px-4 py-2.5 bg-white border border-slate-300 rounded-xl text-sm font-semibold focus:outline-none focus:ring-2 focus:ring-blue-500" 
                />
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-500 mb-1">SEO Sayfa Açıklaması (Meta Description)</label>
                <input 
                  type="text" 
                  value={formData.seo_description} 
                  onChange={(e) => setFormData({ ...formData, seo_description: e.target.value })}
                  placeholder="Arama motorlarında görünecek kısa açıklama..."
                  className="w-full px-4 py-2.5 bg-white border border-slate-300 rounded-xl text-sm font-semibold focus:outline-none focus:ring-2 focus:ring-blue-500" 
                />
              </div>
            </div>
          </div>

          <div>
            <label className="block text-xs font-bold text-slate-500 mb-1.5">Sayfa İçeriği *</label>
            <RichTextEditor 
              value={formData.content}
              onChange={(html) => setFormData({ ...formData, content: html })}
            />
          </div>

          <div className="pt-4 border-t border-slate-200">
            <button 
              type="submit"
              disabled={loading}
              className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-6 py-3.5 rounded-xl text-sm font-bold shadow-lg shadow-blue-600/10 transition-all disabled:opacity-50"
            >
              <Save size={18} /> Sayfayı Kaydet
            </button>
          </div>
        </form>
      ) : (
        /* List Section */
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {pages.map((page) => (
            <div key={page.id} className="bg-slate-50 border border-slate-200/60 p-6 rounded-2xl flex flex-col justify-between group hover:border-blue-300 hover:bg-white transition-all duration-300 relative shadow-sm">
              <div>
                <div className="flex items-start justify-between mb-4">
                  <div className="w-10 h-10 rounded-xl bg-blue-50 flex items-center justify-center text-blue-600">
                    <FileText size={20} />
                  </div>
                  <div className="flex gap-1.5 opacity-0 group-hover:opacity-100 transition-opacity">
                    <button 
                      onClick={() => handleEdit(page)}
                      className="text-blue-600 hover:bg-blue-50 p-2 rounded-xl transition"
                    >
                      <Edit size={16} />
                    </button>
                    <button 
                      onClick={() => handleDelete(page.id)}
                      className="text-red-500 hover:bg-red-50 p-2 rounded-xl transition"
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                </div>
                
                <h3 className="font-extrabold text-slate-800 text-base mb-1 truncate">{page.title}</h3>
                <span className="text-xs text-blue-600 font-bold bg-blue-50 px-2 py-0.5 rounded">/sayfa/{page.slug}</span>
                <p className="text-xs text-slate-500 mt-3 line-clamp-3 leading-relaxed" dangerouslySetInnerHTML={{ __html: page.content.replace(/<[^>]*>/g, '') }}></p>
              </div>

              <div className="border-t border-slate-200/60 pt-4 mt-6 flex justify-between items-center text-[10px] font-bold text-slate-400">
                <span>Son Güncelleme: {page.updated_at ? new Date(page.updated_at).toLocaleDateString('tr-TR') : 'Bilinmiyor'}</span>
                <a 
                  href={`/sayfa/${page.slug}`} 
                  target="_blank" 
                  rel="noreferrer" 
                  className="flex items-center gap-1 text-blue-600 hover:underline"
                >
                  Görüntüle <ChevronRight size={10} />
                </a>
              </div>
            </div>
          ))}
          {pages.length === 0 && (
            <div className="col-span-2 text-center text-slate-400 italic py-12">Herhangi bir sayfa bulunmuyor.</div>
          )}
        </div>
      )}
    </div>
  );
};

export default AdminPages;
